import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { ArrowRight } from "lucide-react"
import { Link } from "react-router-dom"

import { SEO } from "@/components/SEO"
import { GenerativeAISchema } from "@/components/schema"
import { PageFAQ } from "@/components/PageFAQ"
import { generativeAIFAQs } from "@/data/faqData"

const challenges = [
    {
        num: "01",
        stat: "85%",
        statLabel: "of GenAI pilots never reach production",
        title: "Uncontrolled Risk",
        description: "Deployment of generative models without strict boundaries creates unacceptable risks for data privacy, IP leakage, and regulatory compliance.",
        painPoints: ["IP exposure", "Data leakage", "No guardrails"],
    },
    {
        num: "02",
        stat: "62%",
        statLabel: "of enterprises lack AI governance frameworks",
        title: "Governance Gaps",
        description: "The inability to track, audit, and explain model decisions prevents enterprises from moving beyond experimental pilots to production workloads.",
        painPoints: ["No audit trail", "Black-box models", "Compliance risk"],
    },
    {
        num: "03",
        stat: "4.7x",
        statLabel: "more integration effort than expected",
        title: "Integration Friction",
        description: "Stochastic AI models often fail when embedded into deterministic enterprise workflows that require reliability and precision.",
        painPoints: ["Hallucinations", "Brittle pipelines", "Low trust"],
    },
]

const approachSteps = [
    {
        num: "01",
        title: "Secure by Design",
        description: "Architectures that prioritize data sovereignty, ensuring your IP never leaves your control with VPC, on-premise, and air-gapped deployment options.",
    },
    {
        num: "02",
        title: "Domain-Specific Intelligence",
        description: "Fine-tuned models and RAG pipelines tailored to your specific industry constraints, terminology, and knowledge base.",
    },
    {
        num: "03",
        title: "Human-in-the-Loop",
        description: "Built-in evaluation, approval workflows, and feedback loops to maintain quality, trust, and continuous improvement in automated outputs.",
    },
]

const deliverableRows = [
    [
        "Enterprise AI Assistants",
        "Knowledge Retrieval (RAG)",
        "Workflow-Embedded AI Agents",
        "AI Governance Frameworks",
        "Model Customization Platforms",
        "Automated Code Refactoring",
        "Prompt Engineering Toolkits",
        "Multi-Modal AI Pipelines",
    ],
    [
        "Document Intelligence & OCR",
        "Conversational AI Platforms",
        "AI-Powered Search & Discovery",
        "Content Generation Engines",
        "Model Fine-Tuning & RLHF",
        "Vector Database Architecture",
        "LLM Orchestration Layers",
        "Evaluation & Benchmarking",
    ],
    [
        "Agentic AI Frameworks",
        "Semantic Knowledge Graphs",
        "AI Safety & Red Teaming",
        "Responsible AI Tooling",
        "Custom GPT Development",
        "AI-Native API Design",
        "Enterprise Copilot Platforms",
        "Real-Time Inference Pipelines",
    ],
]

const CognitiveEnterprise = () => {
    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="Generative AI Products Built for Enterprise Use | RSA Tech Group"
                description="We build production-ready GenAI platforms and accelerators that help enterprises adopt AI safely, responsibly, and at scale."
                image="https://images.unsplash.com/photo-1677442136019-21780ecad995?q=80&w=2832&auto=format&fit=crop"
            />
            <GenerativeAISchema />
            <Navigation />

            {/* Hero Section */}
            <div className="relative pt-48 pb-24 overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_30%,rgba(28,43,110,0.3),transparent_60%)]" />
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#1C2B6E]/10 rounded-full blur-[150px]" />
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#FDA400]/[0.04] rounded-full blur-[120px]" />

                <div className="section-container relative z-10 text-center">
                    <div className="max-w-4xl mx-auto">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-6">
                            Generative AI
                        </p>
                        <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-[1.1] tracking-tight">
                            Generative AI Products Built for{" "}
                            <span className="text-[#FDA400]">Enterprise Use</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto mb-12 font-light leading-relaxed">
                            We build production-ready GenAI platforms and accelerators that help enterprises adopt AI safely, responsibly, and at scale.
                        </p>
                        <Link
                            to="/contact"
                            className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-[#1C2B6E] bg-[#FDA400] rounded-full hover:bg-[#f5b800] transition-all duration-300 shadow-[0_8px_30px_rgba(253,164,0,0.3)] hover:shadow-[0_12px_40px_rgba(253,164,0,0.4)] hover:-translate-y-0.5"
                        >
                            Talk to a Generative AI Expert <ArrowRight className="ml-2 h-5 w-5" />
                        </Link>
                    </div>
                </div>
            </div>

            {/* The Challenge */}
            <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.2]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />
                <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />
                <div className="absolute bottom-0 right-1/3 w-[400px] h-[400px] bg-[#FDA400]/[0.02] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="text-center mb-20">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            The Problem
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
                            The Enterprise GenAI{" "}
                            <span className="text-[#FDA400]">Challenge</span>
                        </h2>
                        <p className="text-slate-500 text-lg mt-6 max-w-3xl mx-auto font-light leading-relaxed">
                            Most enterprises are stuck in pilot purgatory - unable to move generative AI from demos to production-grade value.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto">
                        {challenges.map((item) => (
                            <div
                                key={item.num}
                                className="partner-card group rounded-3xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_80px_rgba(28,43,110,0.12)] transition-all duration-500 hover:-translate-y-2"
                            >
                                <div className="absolute inset-0 z-20 overflow-hidden rounded-3xl pointer-events-none">
                                    <div className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-[shine_0.8s_ease-in-out]" />
                                </div>
                                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FDA400] to-[#f5b800] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-10" />
                                <div className="absolute -top-16 -right-16 w-44 h-44 bg-[#FDA400]/[0.04] rounded-full blur-[50px] group-hover:bg-[#FDA400]/[0.10] group-hover:scale-125 transition-all duration-700" />
                                <span className="absolute top-6 right-8 text-[120px] font-black text-[#1C2B6E]/[0.03] leading-none select-none group-hover:text-[#1C2B6E]/[0.06] transition-colors duration-500">
                                    {item.num}
                                </span>

                                <div className="p-8 md:p-10 h-full flex flex-col relative z-10">
                                    <div className="mb-6">
                                        <div className="text-5xl md:text-6xl font-black text-[#FDA400] leading-none mb-1">
                                            {item.stat}
                                        </div>
                                        <div className="text-[10px] text-slate-400 uppercase tracking-[0.15em] font-semibold">
                                            {item.statLabel}
                                        </div>
                                    </div>

                                    <h3 className="text-2xl font-black text-[#1C2B6E] tracking-tight mb-3">
                                        {item.title}
                                    </h3>
                                    <p className="text-slate-500 text-sm leading-relaxed font-light mb-6">
                                        {item.description}
                                    </p>

                                    <div className="flex flex-wrap gap-2 mt-auto pt-6 border-t border-slate-100">
                                        {item.painPoints.map((point, i) => (
                                            <span
                                                key={i}
                                                className="px-3 py-1.5 bg-[#1C2B6E]/[0.04] text-[#1C2B6E]/60 text-xs font-medium rounded-full border border-[#1C2B6E]/[0.06] hover:bg-[#FDA400]/10 hover:border-[#FDA400]/30 hover:text-[#FDA400] transition-all duration-300 cursor-default"
                                            >
                                                {point}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Product-Led GenAI Approach */}
            <section className="py-24 relative overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(28,43,110,0.15),transparent_70%)]" />
                <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#FDA400]/[0.03] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
                        <div>
                            <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                                Our Methodology
                            </p>
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1] mb-6">
                                Product-Led{" "}
                                <span className="text-[#FDA400]">GenAI</span>{" "}
                                Approach
                            </h2>
                            <p className="text-slate-400 text-lg font-light leading-relaxed mb-10">
                                We build production-ready GenAI products with enterprise-grade security, governance, and integration - not science projects that never ship.
                            </p>

                            <div className="space-y-8">
                                {approachSteps.map((step) => (
                                    <div key={step.num} className="flex gap-5 group">
                                        <div className="w-12 h-12 bg-[#FDA400]/10 border border-[#FDA400]/20 rounded-full flex items-center justify-center flex-shrink-0">
                                            <span className="text-sm font-black text-[#FDA400]">{step.num}</span>
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-black text-white tracking-tight mb-2">{step.title}</h3>
                                            <p className="text-slate-400 text-sm font-light leading-relaxed">{step.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* GenAI Platform Visualization */}
                        <div className="relative">
                            <div className="absolute inset-0 bg-[#FDA400]/[0.03] rounded-3xl blur-[60px]" />
                            <div className="relative bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] rounded-3xl p-8 md:p-10">
                                <div className="flex items-center justify-between mb-8 border-b border-white/10 pb-4">
                                    <div className="text-xs text-slate-400 uppercase tracking-[0.2em] font-bold">GenAI Platform Stack</div>
                                    <div className="flex items-center gap-2">
                                        <span className="relative flex h-2 w-2">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FDA400] opacity-40" />
                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FDA400]" />
                                        </span>
                                        <span className="text-[#FDA400] text-xs font-bold">Production</span>
                                    </div>
                                </div>

                                {/* Platform Architecture */}
                                <div className="space-y-3">
                                    {/* Application Layer */}
                                    <div className="bg-[#FDA400]/10 border border-[#FDA400]/20 rounded-2xl p-4">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-8 h-8 bg-[#FDA400]/20 rounded-lg flex items-center justify-center">
                                                <span className="text-[#FDA400] text-[10px] font-black">APP</span>
                                            </div>
                                            <span className="text-white text-sm font-bold">AI Assistants & Agents</span>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {["Copilots", "Search", "Content Gen", "Code AI"].map((t) => (
                                                <span key={t} className="px-2 py-1 bg-[#FDA400]/10 border border-[#FDA400]/15 rounded-md text-[10px] font-semibold text-[#FDA400]/80">{t}</span>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Connection */}
                                    <div className="flex justify-center">
                                        <div className="w-px h-4 bg-gradient-to-b from-[#FDA400]/40 to-[#1C2B6E]/40" />
                                    </div>

                                    {/* Orchestration Layer */}
                                    <div className="bg-[#1C2B6E]/20 border border-[#1C2B6E]/30 rounded-2xl p-4">
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-8 h-8 bg-[#1C2B6E]/30 rounded-lg flex items-center justify-center">
                                                <span className="text-white text-[10px] font-black">RAG</span>
                                            </div>
                                            <span className="text-white text-sm font-bold">Retrieval & Orchestration</span>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {["Vector DB", "Embeddings", "Chunking", "Reranking"].map((t) => (
                                                <span key={t} className="px-2 py-1 bg-white/[0.06] border border-white/[0.08] rounded-md text-[10px] font-semibold text-slate-400">{t}</span>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Connection */}
                                    <div className="flex justify-center">
                                        <div className="w-px h-4 bg-gradient-to-b from-[#1C2B6E]/40 to-white/10" />
                                    </div>

                                    {/* Foundation Layer */}
                                    <div className="grid grid-cols-3 gap-3">
                                        {[
                                            { label: "LLMs", sub: "GPT / Claude / Llama" },
                                            { label: "Guard", sub: "Safety & RBAC" },
                                            { label: "Eval", sub: "Metrics & Logs" },
                                        ].map((item) => (
                                            <div key={item.label} className="bg-white/[0.06] border border-white/[0.08] rounded-xl p-3 text-center hover:bg-white/[0.10] hover:border-[#FDA400]/20 transition-all duration-300 group/item cursor-default">
                                                <span className="text-xs font-black text-slate-400 group-hover/item:text-[#FDA400] transition-colors block">{item.label}</span>
                                                <span className="text-[9px] text-slate-500 mt-1 block">{item.sub}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="mt-8 pt-6 border-t border-white/10 grid grid-cols-3 gap-4">
                                    {[
                                        { value: "< 2s", label: "Avg Response" },
                                        { value: "99.5%", label: "Accuracy (RAG)" },
                                        { value: "0", label: "Data Leaks" },
                                    ].map((stat, i) => (
                                        <div key={i} className="text-center">
                                            <div className="text-lg font-black text-[#FDA400]">{stat.value}</div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold">{stat.label}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* What We Deliver - Scrolling Marquee */}
            <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.15]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />
                <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />
                <div className="absolute bottom-0 left-1/3 w-[400px] h-[400px] bg-[#FDA400]/[0.02] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="text-center mb-16">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Products & Accelerators
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
                            What We{" "}
                            <span className="text-[#FDA400]">Deliver</span>
                        </h2>
                        <p className="text-slate-500 text-lg mt-6 max-w-2xl mx-auto font-light leading-relaxed">
                            Production-ready GenAI products and accelerators designed for enterprise-grade adoption at scale.
                        </p>
                    </div>
                </div>

                <div className="relative z-10 space-y-4">
                    <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white via-white/80 to-transparent z-20 pointer-events-none" />
                    <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white via-white/80 to-transparent z-20 pointer-events-none" />

                    {deliverableRows.map((row, rowIndex) => (
                        <div key={rowIndex} className="overflow-hidden">
                            <div
                                className="flex gap-4 w-max"
                                style={{
                                    animation: `${rowIndex % 2 === 0 ? "scrollLeft" : "scrollRight"} ${30 + rowIndex * 5}s linear infinite`,
                                }}
                            >
                                {[...row, ...row].map((item, i) => (
                                    <div
                                        key={i}
                                        className="flex items-center gap-3 px-6 py-4 bg-white rounded-2xl border border-[#1C2B6E]/[0.06] shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:border-[#FDA400]/30 hover:shadow-[0_12px_40px_rgba(28,43,110,0.1)] transition-all duration-300 group cursor-default flex-shrink-0"
                                    >
                                        <span className="relative flex h-2 w-2 flex-shrink-0">
                                            <span className="absolute inline-flex h-full w-full rounded-full bg-[#FDA400] opacity-30 group-hover:animate-ping" />
                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FDA400]" />
                                        </span>
                                        <span className="text-[#1C2B6E] font-bold text-sm whitespace-nowrap group-hover:text-[#FDA400] transition-colors duration-300">
                                            {item}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Enterprise Ready + Engagement Model */}
            <section className="py-24 relative overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(28,43,110,0.15),transparent_70%)]" />
                <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#FDA400]/[0.03] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    {/* Enterprise Ready */}
                    <div className="text-center mb-16">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Built for Scale
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1]">
                            Enterprise Ready{" "}
                            <span className="text-[#FDA400]">by Design</span>
                        </h2>
                        <p className="text-slate-400 text-lg mt-6 max-w-2xl mx-auto font-light leading-relaxed">
                            Every product is architected for sovereign deployment, strict governance, and seamless integration with your existing enterprise systems.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-24">
                        {[
                            { num: "01", title: "Sovereign Infrastructure", desc: "Full model sovereignty with flexible deployment options including VPC, on-premise, and air-gapped environments for strict data residency.", tags: ["VPC Deploy", "On-Prem", "Air-Gapped", "Data Residency"] },
                            { num: "02", title: "Governance & Compliance", desc: "Comprehensive audit logging, role-based access controls, and automated approval workflows aligned with enterprise compliance standards.", tags: ["RBAC", "Audit Logs", "SOC2", "GDPR"] },
                        ].map((item) => (
                            <div
                                key={item.num}
                                className="relative bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] rounded-3xl p-8 md:p-10 hover:bg-white/[0.07] hover:border-[#FDA400]/20 transition-all duration-500 group"
                            >
                                <span className="absolute top-4 right-6 text-7xl font-black text-white/[0.03] leading-none select-none group-hover:text-white/[0.06] transition-colors duration-500">
                                    {item.num}
                                </span>
                                <div className="relative z-10">
                                    <div className="text-xs font-bold text-[#FDA400] uppercase tracking-[0.2em] mb-4">
                                        {item.num}
                                    </div>
                                    <h3 className="text-2xl font-black text-white tracking-tight mb-3">{item.title}</h3>
                                    <p className="text-slate-400 text-sm font-light leading-relaxed mb-6">{item.desc}</p>
                                    <div className="flex flex-wrap gap-2 pt-5 border-t border-white/[0.06]">
                                        {item.tags.map((tag, i) => (
                                            <span key={i} className="px-3 py-1.5 bg-white/[0.04] text-slate-400 text-xs font-medium rounded-full border border-white/[0.08] hover:bg-[#FDA400]/10 hover:border-[#FDA400]/30 hover:text-[#FDA400] transition-all duration-300 cursor-default">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Engagement Model */}
                    <div className="text-center mb-16">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Our Process
                        </p>
                        <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight leading-[1.1]">
                            Strategy-Led{" "}
                            <span className="text-[#FDA400]">Enablement</span>
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 relative max-w-5xl mx-auto">
                        <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-[2px] bg-gradient-to-r from-[#FDA400]/40 via-[#1C2B6E]/30 to-[#FDA400]/40 z-0" />

                        {[
                            { num: "01", title: "Discover & Define", desc: "Identify high-value use cases and define the products needed to solve your specific challenges." },
                            { num: "02", title: "Configure & Deploy", desc: "Tailor our accelerators to your environment, integrating them securely with your data and workflows." },
                            { num: "03", title: "Adopt & Optimize", desc: "Support rollout and adoption, continuously refining models to improve performance and accuracy." },
                        ].map((step) => (
                            <div key={step.num} className="relative z-10 text-center">
                                <div className="w-28 h-28 mx-auto bg-[#0f1629] border-2 border-[#FDA400]/30 rounded-full flex items-center justify-center mb-8 shadow-[0_0_30px_rgba(253,164,0,0.1)]">
                                    <span className="text-3xl font-black text-[#FDA400]">{step.num}</span>
                                </div>
                                <h3 className="text-xl font-black text-white tracking-tight mb-3">
                                    {step.title}
                                </h3>
                                <p className="text-slate-400 text-sm font-light leading-relaxed max-w-xs mx-auto">
                                    {step.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <PageFAQ
                faqs={generativeAIFAQs}
                title="Generative AI FAQs"
                subtitle="Common questions about enterprise generative AI implementation"
                variant="muted"
            />

            {/* CTA */}
            <section className="py-28 relative overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(28,43,110,0.2),transparent_70%)]" />
                <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-[#FDA400]/[0.04] rounded-full blur-[150px]" />

                <div className="section-container relative z-10 text-center">
                    <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-6">
                        Get Started
                    </p>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1] mb-6">
                        Move GenAI from Experimentation{" "}
                        <span className="text-[#FDA400]">to Production</span>
                    </h2>
                    <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto font-light leading-relaxed">
                        Partner with RSA Tech Group to deploy secure, enterprise-grade AI products that drive real value.
                    </p>
                    <Link
                        to="/contact"
                        className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-[#1C2B6E] bg-[#FDA400] rounded-full hover:bg-[#f5b800] transition-all duration-300 shadow-[0_8px_30px_rgba(253,164,0,0.3)] hover:shadow-[0_12px_40px_rgba(253,164,0,0.4)] hover:-translate-y-0.5"
                    >
                        Talk to a Generative AI Expert <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    )
}

export default CognitiveEnterprise
